import { GetDocumentConnectionsResponse } from '@api/utrv';
import { ConnectionsGroupWrapper } from './ConnectionsGroupWrapper';
import { ConnectionAnswer } from './ConnectionAnswer';
import classNames from 'classnames';
import { useToggle } from '@hooks/useToggle';
import { Badge, Button } from 'reactstrap';
import { UtrvDocumentConnection } from '@g17eco/types/documentUniversalTrackerMapping';
import { DocumentItem } from '@g17eco/types/document';
import { SelectedConnection, useConnectionContext } from './ConnectionContext';
import IconButton from '@g17eco/molecules/button/IconButton';
import { filterAiProcessingDocuments } from '@utils/files';
import { documentOptions } from '@features/document-library/utils';
import { TableColumnType, UniversalTrackerPlain, UtrValueType } from '@g17eco/types/universalTracker';
import { EvidenceFile } from '@g17eco/types/file';
import { mapDocumentsToEvidenceFiles } from '@utils/evidence';
import { DocumentModal, DocumentViewMode } from '@features/document-library/DocumentModal';

const getRelevance = (score: number): { label: string; className: string } => {
  if (score >= 0.8) {
    return { label: 'High', className: 'text-ThemeSuccessMedium' };
  }
  if (score >= 0.5) {
    return { label: 'Medium', className: 'text-ThemeWarningDark' };
  }
  return { label: 'Low', className: 'text-ThemeTextMedium' };
};

interface ConnectionProps
  extends Pick<Props, 'selectedConnection' | 'onClickConnection' | 'onPopulateInput' | 'utr' | 'files' | 'setFiles'> {
  connection: UtrvDocumentConnection;
  document: DocumentItem;
}
const Connection = ({
  connection,
  document,
  selectedConnection,
  onClickConnection,
  onPopulateInput,
  utr,
  files,
  setFiles,
}: ConnectionProps) => {
  const [openDocumentModal, toggleDocumentModal] = useToggle(false);
  const { isReadOnly } = useConnectionContext();
  const isAddedToEvidence = files.some((file) => '_id' in file && file._id === connection.documentId);

  const relevance = getRelevance(connection.score);

  // Support populate for text and text table column only.
  const isTextValueTypeConnection =
    utr.valueType === UtrValueType.Text ||
    (utr.valueType === UtrValueType.Table &&
      connection.valueListCode &&
      utr.valueValidation?.table?.columns.find((col) => col.code === connection.valueListCode)?.type ===
        TableColumnType.Text);

  const documentType = documentOptions.find((o) => o.value === document.ownerSubType)?.label;

  return (
    <div
      className={classNames('p-1', {
        'cursor-pointer': connection.valueListCode,
        'background-ThemeAccentExtralight': selectedConnection?._id === connection._id,
      })}
      onClick={() => onClickConnection(connection)}
      key={connection._id}
    >
      <ConnectionAnswer
        answerText={connection.value ?? 'No answer'}
        showPopulationButton={Boolean(isTextValueTypeConnection)}
        onPopulateInput={() => onPopulateInput(connection)}
      />
      <div className='d-flex justify-content-between align-items-center mt-2 gap-2'>
        {/* TODO: Check for reference too */}
        {isAddedToEvidence ? (
          <Badge pill color='success' className='connections__added-to-evidence-badge text-xs fw-normal px-2 py-0'>
            Added to Evidence
          </Badge>
        ) : null}
        {!isAddedToEvidence && !isReadOnly ? (
          <div className='d-flex flex-nowrap align-items-center'>
            <IconButton
              color='transparent'
              onClick={() => setFiles(mapDocumentsToEvidenceFiles([document]))}
              icon='fa-light fa-plus'
              size='sm'
            />
            <span className='text-nowrap text-xs text-ThemeTextMedium ms-2'>Add to Evidence</span>
          </div>
        ) : null}
        <Button color='link' className='text-sm text-nowrap text-truncate' onClick={toggleDocumentModal}>
          {document.title}
        </Button>
      </div>
      {connection.referencePages?.length ? (
        <div className='text-right text-xs text-ThemeTextMedium'>
          reference: {connection.referencePages.map((page) => `p${page}`).join(', ')}
        </div>
      ) : null}
      <div className='d-flex justify-content-between align-items-center mt-2'>
        <span className='text-xs text-ThemeTextMedium'>
          Relevance: <span className={relevance.className}>{relevance.label}</span>
        </span>
        {documentType ? (
          <Badge pill className='connections__document-type-badge text-xs fw-normal px-2 py-0'>
            {documentType}
          </Badge>
        ) : null}
      </div>
      <DocumentModal
        isOpen={openDocumentModal}
        toggle={toggleDocumentModal}
        document={document}
        mode={DocumentViewMode.View}
        handleUpdate={() => {}}
        handleDelete={() => {}}
      />
    </div>
  );
};

interface Props extends GetDocumentConnectionsResponse {
  selectedConnection?: SelectedConnection;
  onClickConnection: (connection: UtrvDocumentConnection) => void;
  onPopulateInput: (connection: UtrvDocumentConnection) => void;
  utr: Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation'>;
  files: EvidenceFile[];
  setFiles: (addFiles: EvidenceFile[]) => void;
}
export const DocumentConnectionsGroup = ({
  connections,
  documents,
  selectedConnection,
  onClickConnection,
  onPopulateInput,
  utr,
  files,
  setFiles,
}: Props) => {
  const title = (
    <div>
      <i className='fa-light fa-folders text-ThemeIconDark me-2' />
      Document Library
    </div>
  );
  
  const validDocuments = filterAiProcessingDocuments(documents);
  // Create map of valid documents for efficient lookup
  const documentMap = new Map(validDocuments.map((document) => [document._id, document]));
  
  return (
    <ConnectionsGroupWrapper title={title}>
      {connections.map((connection) => {
        const document = documentMap.get(connection.documentId);
        return document && connection.value ? (
          <Connection
            key={connection._id}
            connection={connection}
            document={document}
            selectedConnection={selectedConnection}
            onClickConnection={onClickConnection}
            onPopulateInput={onPopulateInput}
            utr={utr}
            files={files}
            setFiles={setFiles}
          />
        ) : null;
      })}
    </ConnectionsGroupWrapper>
  );
};
